/**
 * MTO (Made-to-Order) localStorage Cache System
 * Efficiently loads and caches metaobject translations
 * Version: 2.0.0
 */

(function() {
    'use strict';
    
    // Configuration
    const CACHE_KEY = 'mto_' + (window.Shopify?.shop?.split('.')[0] || 'unknown') + '_v2';
    const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
    let DEBUG = window.MTO_DEBUG || false;
    
    // Track initialization state
    let isInitialized = false;
    const initializationCallbacks = [];
    
    /**
     * Initialize MTO cache system with provided metaobjects
     * @param {Object} options - Configuration options
     * @param {Array} [options.metaobjects] - Array of metaobject data
     * @param {boolean} [options.debug] - Enable debug logging
     */
    function init(options = {}) {
        DEBUG = options.debug !== undefined ? options.debug : DEBUG;
        window.MTO_DEBUG = DEBUG;
        
        // Ensure theme object exists
        window.theme = window.theme || {};
        
        // Initialize MTO lookup structure
        if (!theme.mtoLookup) {
            theme.mtoLookup = { 
                byVariant: {},
                metaobjects: []
            };
        }
        
        // Process provided metaobjects if any - ALWAYS prioritize fresh metaobjects
        if (options.metaobjects?.length > 0) {
            if (DEBUG) console.log('📦 Initializing MTO cache:', { metaobjects: options.metaobjects.length });
            
            // Store raw metaobjects for debugging/validation
            window.mtoMetaobjectsCache = options.metaobjects;
            
            // ALWAYS clear old cache when new metaobjects are provided
            localStorage.removeItem(CACHE_KEY);
            theme.mtoLookup.byVariant = {};
            theme.mtoLookup.metaobjects = [];
            
            // Reset initialization state to allow processing
            isInitialized = false;
            
            // Process the fresh metaobjects
            processMetaobjects(options.metaobjects);
            
            isInitialized = true;
            notifyInitializationComplete();
        } else if (!isInitialized && loadFromCache()) {
            // Only load from cache if not already initialized and no metaobjects provided
            if (DEBUG) console.log('✅ MTO cache loaded from localStorage');
            isInitialized = true;
            notifyInitializationComplete();
        } else if (!isInitialized) {
            if (DEBUG) console.warn('⚠️ No metaobjects provided and no cache available');
            isInitialized = true;
            notifyInitializationComplete();
        } else {
            if (DEBUG) console.log('ℹ️ MTO Cache already initialized');
        }
    }
    
    /**
     * Process metaobjects and build lookup tables
     * @param {Array} metaobjects - Array of metaobject data
     */
    function processMetaobjects(metaobjects) {
        if (!metaobjects || !Array.isArray(metaobjects)) {
            if (DEBUG) console.error('❌ Invalid metaobjects data');
            return;
        }
        
        const variantLookup = {};
        const metaobjectList = [];
        let totalVariants = 0;
        
        metaobjects.forEach((mto, index) => {
            if (!mto) return;
            
            const identifier = mto.product_identifer || mto.product_identifier || `metaobject-${index}`;
            const translations = {
                en: mto.text_in_english || '',
                cs: mto.text_in_czech || mto.text_in_english || '',
                de: mto.text_in_german || mto.text_in_english || '',
                fr: mto.text_in_french || mto.text_in_english || '',
                source: identifier,
                updatedAt: Date.now()
            };
            
            const variantIds = [];
            let variantCount = 0;
            
            // Process variants if available
            if (mto.product_list?.value?.length) {
                if (DEBUG) {
                    console.log(`📦 Processing metaobject "${identifier}": ${mto.product_list.value.length} variants`);
                }
                mto.product_list.value.forEach(variant => {
                    if (!variant) return;
                    
                    // Handle different ID formats - check id, numeric_id, and gid fields
                    let idStr = String(variant.id || variant.numeric_id || variant.gid || '');
                    
                    // If it's a GID, extract the numeric part
                    if (idStr.startsWith('gid://')) {
                        const gidMatch = idStr.match(/\/(\d+)$/);
                        if (gidMatch) {
                            idStr = gidMatch[1];
                        }
                    }
                    
                    const idNum = idStr.replace(/\D/g, ''); // Extract numbers only
                    
                    // Map all possible ID formats to the same translations
                    const idFormats = [
                        idNum,                              // Numeric ID (most common)
                        idStr,                              // Original ID string
                        `gid://shopify/ProductVariant/${idNum}`,  // GID with numeric
                        `gid://shopify/ProductVariant/${idStr}`   // GID with original
                    ].filter(Boolean);
                    
                    idFormats.forEach(id => {
                        if (!variantLookup[id]) {
                            variantLookup[id] = translations;
                            variantIds.push(id);
                            variantCount++;
                            totalVariants++;
                            
                            
                        }
                    });
                });
            }
            
            // Store metaobject info
            metaobjectList.push({
                id: mto.id || `mto-${index}`,
                identifier: identifier,
                variantCount: variantCount,
                firstFewVariants: variantIds.slice(0, 2)
            });
            
            
        });
        
        // Update global state
        theme.mtoLookup.byVariant = variantLookup;
        theme.mtoLookup.metaobjects = metaobjectList;
        
        // Cache the results
        saveToCache(variantLookup);
        
        if (DEBUG) {
            const totalKeys = Object.keys(variantLookup).length;
            console.log('✅ MTO cache built:', { metaobjects: metaobjectList.length, mappings: totalKeys });
        }
        
        // Notify that initialization is complete
        document.dispatchEvent(new CustomEvent('mtoCacheReady'));
    }
    
    /**
     * Load MTO data from localStorage cache
     * @returns {boolean} true if cache was valid and loaded
     */
    function loadFromCache() {
        try {
            const cached = JSON.parse(localStorage.getItem(CACHE_KEY));
            
            if (!cached || !cached.data || !cached.timestamp) {
                if (DEBUG) console.log('ℹ️ No valid cache found');
                return false;
            }
            
            // Check if cache is still valid
            const now = Date.now();
            if (now - cached.timestamp > CACHE_DURATION) {
                if (DEBUG) console.log('📅 MTO cache expired');
                localStorage.removeItem(CACHE_KEY);
                return false;
            }
            
            // Check cache version
            if (cached.version !== '2.0') {
                if (DEBUG) console.log('🔄 Cache version mismatch, rebuilding...');
                localStorage.removeItem(CACHE_KEY);
                return false;
            }
            
            // Load cached data into theme lookup
            theme.mtoLookup.byVariant = cached.data || {};
            
            if (DEBUG) {
                console.log(`✅ Loaded ${Object.keys(cached.data).length} variant mappings from cache`);
            }
            
            return true;
            
        } catch (error) {
            if (DEBUG) console.error('❌ MTO cache load error:', error);
            localStorage.removeItem(CACHE_KEY);
            return false;
        }
    }
    
    /**
     * Save MTO data to localStorage cache
     * @param {Object} data - The data to cache
     */
    function saveToCache(data) {
        try {
            const cacheData = {
                data: data,
                timestamp: Date.now(),
                version: '2.0',
                shop: window.Shopify?.shop
            };
            
            localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
            
            if (DEBUG) console.log('💾 MTO data cached successfully');
            
        } catch (error) {
            if (DEBUG) console.error('❌ MTO cache save error:', error);
        }
    }
    
    /**
     * Get MTO text for a variant
     * @param {string} locale - Language code (en, cs, de, fr)
     * @param {string|number} variantId - Variant ID to look up
     * @returns {string} Translated text or fallback
     */
    function getText(locale = 'en', variantId) {
        if (!variantId && variantId !== 0) {
            if (DEBUG) console.warn('⚠️ getText called without variantId');
            return getFallbackText(locale);
        }
        
        // Ensure cache is initialized
        if (!isInitialized || !theme.mtoLookup || !theme.mtoLookup.byVariant) {
            
            return getFallbackText(locale);
        }
        
        const variantIdStr = String(variantId);
        const variantKey = findVariantKey(variantIdStr);
        
        if (!variantKey) {
            if (DEBUG) {
                const numId = variantIdStr.replace(/\D/g, '');
                const allKeys = Object.keys(theme.mtoLookup.byVariant || {});
                let foundInRaw = false;
                if (window.mtoMetaobjectsCache && Array.isArray(window.mtoMetaobjectsCache)) {
                    for (let i = 0; i < window.mtoMetaobjectsCache.length; i++) {
                        const mto = window.mtoMetaobjectsCache[i];
                        if (mto.product_list && mto.product_list.value) {
                            const hit = mto.product_list.value.some(v => {
                                const vId = String(v.id || v.numeric_id || v.gid || '');
                                const vNum = vId.replace(/\D/g, '');
                                return vNum === numId || vId === variantIdStr || vId.indexOf(numId) >= 0;
                            });
                            if (hit) { foundInRaw = true; break; }
                        }
                    }
                }
                console.warn('⚠️ MTO miss:', { id: variantIdStr, cacheSize: allKeys.length, rawMatch: foundInRaw });
            }
            return getFallbackText(locale);
        }
        
        const translations = theme.mtoLookup.byVariant[variantKey];
        const lang = translations[locale] ? locale : 'en';
        const text = translations[lang] || translations.en;
        
        
        
        return text || getFallbackText(locale);
    }
    
    /**
     * Find the correct variant key in the lookup table
     * @param {string} variantId - Variant ID to find
     * @returns {string|undefined} The matching key or undefined
     */
    function findVariantKey(variantId) {
        if (!variantId) return undefined;
        
        const strId = String(variantId);
        const numId = strId.replace(/\D/g, '');
        const gid = strId.startsWith('gid://') ? strId : `gid://shopify/ProductVariant/${numId}`;
        
        // Try different key formats
        return [
            strId,                              // Original ID
            numId,                             // Numeric ID only
            gid,                               // Full GID
            `gid://shopify/ProductVariant/${strId}`,  // GID with original ID
            `gid://shopify/ProductVariant/${numId}`   // GID with numeric ID
        ].find(key => key && theme.mtoLookup.byVariant[key]);
    }
    
    /**
     * Get fallback text when no MTO text is found
     * @param {string} locale - Language code
     * @returns {string} Fallback text
     */
    function getFallbackText(locale) {
        const fallbacks = {
            en: theme.strings?.waitingForStock || 'Currently unavailable',
            cs: 'Momentálně nedostupné',
            de: 'Derzeit nicht verfügbar',
            fr: 'Actuellement indisponible'
        };
        
        return fallbacks[locale] || fallbacks.en;
    }
    
    /**
     * Clear the MTO cache
     */
    function clearCache() {
        try {
            localStorage.removeItem(CACHE_KEY);
            if (theme.mtoLookup) {
                theme.mtoLookup.byVariant = {};
                theme.mtoLookup.metaobjects = [];
            }
            window.mtoMetaobjectsCache = [];
            isInitialized = false;
            if (DEBUG) console.log('🧹 MTO cache cleared');
            return true;
        } catch (error) {
            if (DEBUG) console.error('❌ Failed to clear MTO cache:', error);
            return false;
        }
    }
    
    /**
     * Search all raw metaobjects for a variant ID
     * @param {string|number} variantId - Variant ID to search for
     * @returns {Object|null} Metaobject info if found, null otherwise
     */
    function searchRawMetaobjects(variantId) {
        if (!window.mtoMetaobjectsCache || !Array.isArray(window.mtoMetaobjectsCache)) {
            console.warn('⚠️ No raw metaobjects cache available');
            return null;
        }
        
        const variantIdStr = String(variantId);
        const numId = variantIdStr.replace(/\D/g, '');
        const results = [];
        
        window.mtoMetaobjectsCache.forEach((mto, index) => {
            if (mto.product_list && mto.product_list.value) {
                mto.product_list.value.forEach((v, vIndex) => {
                    const vId = String(v.id || v.numeric_id || v.gid || '');
                    const vNum = vId.replace(/\D/g, '');
                    
                    if (vNum === numId || vId === variantIdStr || vId.includes(numId) || vNum === variantIdStr) {
                        results.push({
                            metaobjectIndex: index,
                            variantIndex: vIndex,
                            product_identifer: mto.product_identifer || mto.product_identifier,
                            variantId: vId,
                            numericId: v.numeric_id,
                            gid: v.gid,
                            rawVariant: v,
                            metaobject: mto
                        });
                    }
                });
            }
        });
        
        return results.length > 0 ? results : null;
    }
    
    /**
     * Debug function to check a specific variant
     * @param {string|number} variantId - Variant ID to check
     */
    function debugVariant(variantId) {
        if (!variantId) {
            console.log('❌ No variant ID provided');
            return;
        }
        
        const variantIdStr = String(variantId);
        const numId = variantIdStr.replace(/\D/g, '');
        const variantKey = findVariantKey(variantIdStr);
        
        if (!variantKey) {
            console.log(`❌ Variant ${variantIdStr} not found in MTO lookup`);
            const allKeys = Object.keys(theme.mtoLookup.byVariant || {});
            console.log('Available variant keys (first 10):', allKeys.slice(0, 10));
            console.log('Total variants in cache:', allKeys.length);
            
            // Check if variant exists in raw metaobjects cache
            const rawResults = searchRawMetaobjects(variantIdStr);
            if (rawResults && rawResults.length > 0) {
                console.log(`✅ Found variant in ${rawResults.length} metaobject(s):`, rawResults);
            } else {
                console.log('❌ Variant not found in any metaobject');
                // Show total variant count across all metaobjects
                if (window.mtoMetaobjectsCache && Array.isArray(window.mtoMetaobjectsCache)) {
                    let totalVariants = 0;
                    window.mtoMetaobjectsCache.forEach(mto => {
                        if (mto.product_list && mto.product_list.value) {
                            totalVariants += mto.product_list.value.length;
                        }
                    });
                    console.log(`📊 Total variants in all metaobjects: ${totalVariants}`);
                }
            }
            return;
        }
        
        const data = theme.mtoLookup.byVariant[variantKey];
        console.log(`🔍 Debug variant ${variantIdStr}:`, {
            foundAs: variantKey,
            source: data.source || 'unknown',
            translations: {
                en: data.en || 'N/A',
                cs: data.cs || 'N/A',
                de: data.de || 'N/A',
                fr: data.fr || 'N/A'
            },
            rawData: data
        });
    }
    
    /**
     * Register a callback to be called when initialization is complete
     * @param {Function} callback - Function to call
     */
    function onReady(callback) {
        if (typeof callback !== 'function') return;
        
        if (isInitialized) {
            callback();
        } else {
            initializationCallbacks.push(callback);
        }
    }
    
    /**
     * Notify all registered callbacks that initialization is complete
     */
    function notifyInitializationComplete() {
        while (initializationCallbacks.length > 0) {
            const callback = initializationCallbacks.shift();
            try {
                callback();
            } catch (error) {
                console.error('MTO callback error:', error);
            }
        }
    }
    
    // Expose public API
    window.MtoCache = {
        // Core functions
        init,
        getText,
        clearCache,
        debugVariant,
        onReady,
        searchRawMetaobjects,
        
        // Force re-initialization (clears cache first)
        reinit: function(options = {}) {
            clearCache();
            isInitialized = false;
            return init(options);
        },
        
        // Version info
        version: '2.0.0',
        
        // Debug mode
        set debug(enabled) {
            DEBUG = !!enabled;
            window.MTO_DEBUG = DEBUG;
        },
        get debug() {
            return DEBUG;
        }
    };
    
    // For backward compatibility
    if (typeof module !== 'undefined' && module.exports) {
        module.exports = window.MtoCache;
    }
    
    // Don't auto-initialize - let theme.liquid handle initialization with fresh metaobjects
    // This prevents loading stale cache before fresh metaobjects are available
    
})();
